<template>
  <div class="dac">
    <div v-if="qr" :class="cls('depQrWrap')">
      <img :src="qr" :class="cls('depQr')" alt="" :data-testid="testidPrefix + '-deposit-qr'" />
    </div>
    <button type="button" :class="cls('depAddr')" :data-testid="testidPrefix + '-deposit-address'" @click="copy">
      <span class="num">{{ address }}</span>
      <IconCopy :size="14" />
    </button>
    <!-- The one essential warning, one short line; everything else is the caller's tap-to-open «Подробнее». -->
    <div :class="cls('hint')" :data-testid="testidPrefix + '-deposit-only-asset'">{{ t('vitrine.topup.deposit.onlyAsset', { asset, network: railNetworkLabel(network) }) }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { IconCopy } from '@/components/ui/icons';
import { railNetworkLabel } from '@/api/topupOptions';

/**
 * The call to send crypto: QR, the address (tap to copy) and «send only this asset on this network». Shared by the
 * deposit rail's top-up request and the concierge crypto payment (CONCIERGE_PAYMENTS_MODE.md §11.1 «адрес и QR тем же
 * компонентом, что у рельса»). The caller decides WHEN to show it (a request that can no longer be credited must not
 * invite a transfer); the component only renders it.
 *
 * `classPrefix` keeps the caller's legacy class names next to the component's own (`tu__depAddr` stays a valid
 * selector on the top-up screen). Copy success is emitted, not shown: the caller owns the toast.
 */
const props = withDefaults(defineProps<{
  address: string;
  asset: string;
  network: string;
  /** data-testid prefix: `<prefix>-deposit-qr`, `<prefix>-deposit-address`. */
  testidPrefix?: string;
  classPrefix?: string;
}>(), {
  testidPrefix: 'topup',
  classPrefix: undefined,
});

const emit = defineEmits<{ copied: [] }>();
const { t } = useI18n();

const qr = ref<string | null>(null);
watch(() => props.address, async (addr) => {
  qr.value = null;
  if (!addr) return;
  try {
    qr.value = await QRCode.toDataURL(addr, { margin: 1, width: 220 });
  } catch { /* QR is a convenience — the address itself stays on screen and copyable */ }
}, { immediate: true });

async function copy() {
  if (!props.address) return;
  try {
    await navigator.clipboard.writeText(props.address);
    emit('copied');
  } catch { /* clipboard denied — the address is on screen and selectable anyway */ }
}

function cls(name: string, modifier?: string): string[] {
  const out = [modifier ? `dac__${name}--${modifier}` : `dac__${name}`];
  if (props.classPrefix) out.push(modifier ? `${props.classPrefix}__${name}--${modifier}` : `${props.classPrefix}__${name}`);
  return out;
}
</script>

<style scoped lang="scss">
/* One type set with the card around it (03 §3.2): 13 px body; the address in --text on --soft, the line in --muted. */
.dac { width: 100%; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.dac__depQrWrap { display: flex; justify-content: center; margin: 6px 0 2px; }
.dac__depQr { width: 180px; height: 180px; border-radius: 12px; background: #fff; padding: 8px; box-shadow: var(--shadow-surface); }
.dac__depAddr {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 12px 14px; border-radius: 12px; border: 0;
  background: var(--soft); color: var(--text);
  font: inherit; font-size: 13px; line-height: 1.45; word-break: break-all; text-align: left; cursor: pointer;
  transition: background var(--dur-fast), transform var(--dur-fast);
  > span { flex: 1 1 auto; min-width: 0; }
  :deep(svg) { flex: 0 0 auto; color: var(--bronze); }
  /* The press itself has to answer: without it the finger went down and the screen did not move. */
  &:active { background: color-mix(in srgb, var(--bronze) 18%, var(--soft)); transform: scale(0.995); }
  &:focus-visible { box-shadow: var(--focus-ring); outline: none; }
}
.dac__hint { font-size: 13px; line-height: 1.45; color: var(--muted); text-align: center; }
</style>
